import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  configurationApi,
  WorkItemTypeMapping,
} from '../../services/configuration-api';

interface WorkItemMappingFormProps {
  initialData?: WorkItemTypeMapping;
  organizationId: string;
  onSave: () => void;
  onCancel: () => void;
}

interface FormData {
  sourceType: string;
  targetCategory: 'NewDevelopment' | 'Maintenance' | 'TechnicalDebt';
  effortEstimation: {
    defaultSize: 'XS' | 'S' | 'M' | 'L' | 'XL';
    customEffort?: number;
  };
  businessValue?: number;
  enabled: boolean;
}

export const WorkItemMappingForm: React.FC<WorkItemMappingFormProps> = ({
  initialData,
  organizationId,
  onSave,
  onCancel,
}) => {
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      sourceType: initialData?.sourceType || '',
      targetCategory: initialData?.targetCategory || 'NewDevelopment',
      effortEstimation: {
        defaultSize: initialData?.effortEstimation?.defaultSize || 'M',
        customEffort: initialData?.effortEstimation?.customEffort,
      },
      businessValue: initialData?.businessValue,
      enabled: initialData?.enabled ?? true,
    },
    mode: 'onChange',
  });

  const saveMutation = useMutation({
    mutationFn: (data: WorkItemTypeMapping) =>
      configurationApi.saveWorkItemMapping(data),
    onSuccess: () => {
      onSave();
    },
    onError: (error: any) => {
      setError(
        error.response?.data?.error || 'Failed to save work item mapping'
      );
    },
  });

  const onSubmit = (data: FormData) => {
    setError(null);

    const mapping: WorkItemTypeMapping = {
      ...data,
      organizationId,
      id: initialData?.id,
      createdAt: initialData?.createdAt,
      updatedAt: initialData?.updatedAt,
    };

    saveMutation.mutate(mapping);
  };

  const targetCategoryOptions = [
    {
      value: 'NewDevelopment',
      label: 'New Development',
      description: 'New features, enhancements, and user stories',
      color: 'primary' as const,
    },
    {
      value: 'Maintenance',
      label: 'Maintenance',
      description: 'Bug fixes, support tasks, and operational work',
      color: 'warning' as const,
    },
    {
      value: 'TechnicalDebt',
      label: 'Technical Debt',
      description: 'Refactoring, infrastructure, and technical improvements',
      color: 'secondary' as const,
    },
  ];

  const tShirtSizes = [
    { value: 'XS', label: 'XS (Extra Small)', effort: 1 },
    { value: 'S', label: 'S (Small)', effort: 2 },
    { value: 'M', label: 'M (Medium)', effort: 5 },
    { value: 'L', label: 'L (Large)', effort: 8 },
    { value: 'XL', label: 'XL (Extra Large)', effort: 13 },
  ];

  const commonWorkItemTypes = [
    'Story',
    'Feature',
    'Epic',
    'Bug',
    'Task',
    'Defect',
    'User Story',
    'Technical Task',
    'Spike',
    'Research',
    'Infrastructure',
    'Documentation',
    'Test Case',
  ];

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Mapping */}
        <Grid size={12}>
          <Typography variant='h6' gutterBottom>
            Work Item Type Mapping
          </Typography>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            Map your organization's work item types to standard categories for
            consistent metrics calculation
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='sourceType'
            control={control}
            rules={{ required: 'Source type is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Source Work Item Type'
                fullWidth
                error={!!errors.sourceType}
                helperText={
                  errors.sourceType?.message ||
                  'The work item type from your source system (e.g., ADO, Jira)'
                }
              />
            )}
          />
          <Box sx={{ mt: 1 }}>
            <Typography variant='caption' color='text.secondary'>
              Common types:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {commonWorkItemTypes.map(type => (
                <Chip
                  key={type}
                  label={type}
                  size='small'
                  variant='outlined'
                  onClick={() => {
                    setValue('sourceType', type, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='targetCategory'
            control={control}
            rules={{ required: 'Target category is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.targetCategory}>
                <InputLabel>Target Category</InputLabel>
                <Select {...field} label='Target Category'>
                  {targetCategoryOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Chip
                            label={option.label}
                            size='small'
                            color={option.color}
                            variant='outlined'
                          />
                        </Box>
                        <Typography variant='caption' color='text.secondary'>
                          {option.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        {/* Effort Estimation */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Effort Estimation
          </Typography>
          <Typography variant='body2' color='text.secondary' gutterBottom>
            Configure how effort is estimated for this work item type
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='effortEstimation.defaultSize'
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Default T-Shirt Size</InputLabel>
                <Select {...field} label='Default T-Shirt Size'>
                  {tShirtSizes.map(size => (
                    <MenuItem key={size.value} value={size.value}>
                      <Box>
                        <Typography variant='body1'>{size.label}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          Effort: {size.effort} points
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='effortEstimation.customEffort'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label='Custom Effort Points (Optional)'
                type='number'
                fullWidth
                helperText='Override default T-shirt size with custom effort points'
                inputProps={{ min: 0, step: 0.5 }}
              />
            )}
          />
        </Grid>

        {/* Business Value */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Business Value
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name='businessValue'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label='Business Value Points (Optional)'
                type='number'
                fullWidth
                helperText='Assign business value points for ROI calculations'
                inputProps={{ min: 0 }}
              />
            )}
          />
        </Grid>

        {/* Settings */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Settings
          </Typography>
        </Grid>

        <Grid size={12}>
          <Controller
            name='enabled'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label='Enable this mapping'
              />
            )}
          />
        </Grid>

        {/* Preview */}
        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant='h6' gutterBottom>
            Mapping Preview
          </Typography>
          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant='body2'>
              <strong>Source:</strong> {watch('sourceType') || 'Not specified'}{' '}
              → <strong>Category:</strong> {watch('targetCategory')}
            </Typography>
            <Typography variant='body2'>
              <strong>Default Size:</strong>{' '}
              {watch('effortEstimation.defaultSize')}
              {watch('effortEstimation.customEffort') && (
                <> (Custom: {watch('effortEstimation.customEffort')} points)</>
              )}
            </Typography>
            {watch('businessValue') && (
              <Typography variant='body2'>
                <strong>Business Value:</strong> {watch('businessValue')} points
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Actions */}
        <Grid size={12}>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}
          >
            <Button onClick={onCancel} disabled={saveMutation.isPending}>
              Cancel
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={!isValid || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Mapping'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
